import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Admin, UpdateAdminDTO } from '@/interface/types/user'
import { services } from '@/lib/api/index'
// import { api } from '@/lib/api/axios-instance'
import FileUploader from '@/components/shared/FileUploader'
import { Eye, EyeOff, Trash2, Check, ChevronsUpDown } from 'lucide-react'
import { FormYearMonthPicker } from '@/components/ui/form-year-month-picker'
import { parseConflictError, formatErrorMessage } from '@/utils/error-handlers'
import { useToast } from '@/components/ui/use-toast'
import { Toaster } from '@/components/ui/toaster'
import { useTranslation } from 'react-i18next'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { X, Filter, ChevronLeft, ChevronRight } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import Loading from '@/components/shared/Loading'
import CdnImage from '@/components/shared/CdnImage'
import { DEFAULT_AVATAR } from '@/constants'
import { AddAdminTour } from '@/tour/superAdminTour/addAdminTour'

interface AdminFilters {
  cin: string
  name: string
}

interface ActiveFilter {
  key: keyof AdminFilters
  value: string
  label: string
}

// Helper function to get all nationality keys from the JSON structure
const getAllNationalityKeys = (): string[] => {
  // These are all the nationality keys from the JSON files
  return [
    'afghan', 'albanian', 'algerian', 'american', 'andorran', 'angolan', 'antiguans',
    'argentine', 'armenian', 'australian', 'austrian', 'azerbaijani', 'bahamian',
    'bahraini', 'bangladeshi', 'barbadian', 'barbudans', 'batswana', 'belarusian',
    'belgian', 'belizean', 'beninese', 'bhutanese', 'bolivian', 'bosnian', 'brazilian',
    'british', 'bruneian', 'bulgarian', 'burkinabe', 'burmese', 'burundian', 'cambodian',
    'cameroonian', 'canadian', 'cape verdean', 'central african', 'chadian', 'chilean',
    'chinese', 'colombian', 'comoran', 'congolese', 'costa rican', 'croatian', 'cuban',
    'cypriot', 'czech', 'danish', 'djibouti', 'dominican', 'dutch', 'east timorese',
    'ecuadorean', 'egyptian', 'emirian', 'equatorial guinean', 'eritrean', 'estonian',
    'ethiopian', 'fijian', 'filipino', 'finnish', 'french', 'gabonese', 'gambian',
    'georgian', 'german', 'ghanaian', 'greek', 'grenadian', 'guatemalan', 'guinea-bissauan',
    'guinean', 'guyanese', 'haitian', 'herzegovinian', 'honduran', 'hungarian', 'icelander',
    'indian', 'indonesian', 'iranian', 'iraqi', 'irish', 'israeli', 'italian', 'ivorian',
    'jamaican', 'japanese', 'jordanian', 'kazakhstani', 'kenyan', 'kittian and nevisian',
    'kuwaiti', 'kyrgyz', 'laotian', 'latvian', 'lebanese', 'liberian', 'libyan',
    'liechtensteiner', 'lithuanian', 'luxembourger', 'macedonian', 'malagasy', 'malawian',
    'malaysian', 'maldivan', 'malian', 'maltese', 'marshallese', 'mauritanian', 'mauritian',
    'mexican', 'micronesian', 'moldovan', 'monacan', 'mongolian', 'moroccan', 'mosotho',
    'motswana', 'mozambican', 'namibian', 'nauruan', 'nepalese', 'new zealander', 'ni-vanuatu',
    'nicaraguan', 'nigerien', 'north korean', 'northern irish', 'norwegian', 'omani',
    'pakistani', 'palauan', 'panamanian', 'papua new guinean', 'paraguayan', 'peruvian',
    'polish', 'portuguese', 'qatari', 'romanian', 'russian', 'rwandan', 'saint lucian',
    'salvadoran', 'samoan', 'san marinese', 'sao tomean', 'saudi', 'scottish', 'senegalese',
    'serbian', 'seychellois', 'sierra leonean', 'singaporean', 'slovakian', 'slovenian',
    'solomon islander', 'somali', 'south african', 'south korean', 'spanish', 'sri lankan',
    'sudanese', 'surinamer', 'swazi', 'swedish', 'swiss', 'syrian', 'taiwanese', 'tajik',
    'tanzanian', 'thai', 'togolese', 'tongan', 'trinidadian or tobagonian', 'tunisian',
    'turkish', 'tuvaluan', 'ugandan', 'ukrainian', 'uruguayan', 'uzbekistani', 'venezuelan',
    'vietnamese', 'welsh', 'yemenite', 'zambian', 'zimbabwean'
  ]
}

export default function SuperAdminManagement(): JSX.Element {
  const { t } = useTranslation('superAdmin')
  const { t: tNationalities } = useTranslation('nationalities')
  const [selectedAdmin, setSelectedAdmin] = useState<Admin | null>(null)
  const [isProfileModalOpen, setIsProfileModalOpen] = useState<boolean>(false)
  const [isAddAdminOpen, setIsAddAdminOpen] = useState<boolean>(false)
  const [currentPage, setCurrentPage] = useState(1)

  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)
  const [isPasswordFocused, setIsPasswordFocused] = useState(false)
  const [adminToDelete, setAdminToDelete] = useState<Admin | null>(null)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  
  // Nationality search state for update form
  const [nationalitySearchOpen, setNationalitySearchOpen] = useState(false)
  const [nationalitySearchQuery, setNationalitySearchQuery] = useState('')
  const [selectedNationality, setSelectedNationality] = useState<string>('')
  
  // Nationality search state for create form
  const [createNationalitySearchOpen, setCreateNationalitySearchOpen] = useState(false)
  const [createNationalitySearchQuery, setCreateNationalitySearchQuery] = useState('')
  const [createSelectedNationality, setCreateSelectedNationality] = useState<string>('tunisian')

  const [uploadedAvatarUrl, setUploadedAvatarUrl] = useState<string | null>(null)
  const [createUploadedAvatarUrl, setCreateUploadedAvatarUrl] = useState<string | null>(null)

  const queryClient = useQueryClient()
  const { toast } = useToast()

  // Sync hidden input when selected nationality changes (update form)
  useEffect(() => {
    if (selectedNationality && getAllNationalityKeys().includes(selectedNationality)) {
      const hiddenInput = document.getElementById('nationality-hidden') as HTMLInputElement
      if (hiddenInput) {
        hiddenInput.value = selectedNationality
      }
    }
  }, [selectedNationality])

  // Sync hidden input when selected nationality changes (create form)
  useEffect(() => {
    if (createSelectedNationality && getAllNationalityKeys().includes(createSelectedNationality)) {
      const hiddenInput = document.getElementById('create-nationality-hidden') as HTMLInputElement
      if (hiddenInput) {
        hiddenInput.value = createSelectedNationality
      }
    }
  }, [createSelectedNationality])

  // Queries
  const {
    data: admins = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['admins'],
    queryFn: services.admin.getAll,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  // const createAdminMutation = useMutation({
  //   mutationFn: (data: CreateAdminDTO) => services.admin.create(data),
  //   onSuccess: () => {
  //     queryClient.invalidateQueries({ queryKey: ['admins'] })
  //     setIsAddAdminOpen(false)
  //     toast({
  //       title: 'Admin Created',
  //       description: 'The new admin has been successfully created.',
  //       variant: 'default',
  //     })
  //   },
  // })

  const toggleActiveMutation = useMutation({
    mutationFn: (id: string) => services.admin.toggleActive(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['admins'] })
      // Find the admin to get their current status
      const admin = admins.find((a) => a.id === id)
      const newStatus = admin?.isActive ? 'deactivated' : 'activated'
      toast({
        title: t('superAdminManagement.notifications.statusUpdated.title'),
        description: t('superAdminManagement.notifications.statusUpdated.description', {
          status: t(`superAdminManagement.notifications.statusUpdated.${newStatus}`)
        }),
        variant: 'default',
      })
    },
    onError: () => {
      toast({
        title: t('superAdminManagement.notifications.statusUpdateFailed.title'),
        description: t('superAdminManagement.notifications.statusUpdateFailed.description'),
        variant: 'destructive',
      })
    },
  })

  const deleteAdminMutation = useMutation({
    mutationFn: (id: string) => services.admin.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admins'] })
      setIsDeleteDialogOpen(false)
      setAdminToDelete(null)
      toast({
        title: t('superAdminManagement.notifications.adminDeleted.title'),
        description: t('superAdminManagement.notifications.adminDeleted.description'),
        variant: 'default',
      })
    },
    onError: () => {
      // onError: (error) => {
      // console.error('Error deleting admin:', error)
      toast({
        title: t('superAdminManagement.notifications.deletionFailed.title'),
        description: t('superAdminManagement.notifications.deletionFailed.description'),
        variant: 'destructive',
      })
      setIsDeleteDialogOpen(false)
    },
  })

  // Filter and search admins
  const filteredAdmins = admins.filter((admin) => {
    // Search query matching
    const searchMatches =
      searchQuery === '' ||
      `${admin.firstname} ${admin.lastname} ${admin.cin}`
        .toLowerCase()
        .includes(searchQuery.toLowerCase())

    // All active filters must match
    const filtersMatch = activeFilters.every((filter) => {
      switch (filter.key) {
        case 'name':
          return `${admin.firstname} ${admin.lastname}`
            .toLowerCase()
            .includes(filter.value.toLowerCase())
        case 'cin':
          return admin.cin.toLowerCase().includes(filter.value.toLowerCase())
        default:
          return true
      }
    })

    return searchMatches && filtersMatch
  })

  // Pagination setup
  const adminsPerPage = 5
  const totalPages = Math.ceil(filteredAdmins.length / adminsPerPage)
  const indexOfLastAdmin = currentPage * adminsPerPage
  const indexOfFirstAdmin = indexOfLastAdmin - adminsPerPage
  const currentAdmins = filteredAdmins.slice(
    indexOfFirstAdmin,
    indexOfLastAdmin
  )

  const handleShowProfile = (admin: Admin) => {
    // console.log('Opening profile for admin:', admin)
    // console.log('Admin address:', admin.address)
    // console.log('Admin phone:', admin.phone)
    // Ensure address and phone are strings (not null/undefined)
    const adminWithDefaults = {
      ...admin,
      address: admin.address ?? '',
      phone: admin.phone ?? '',
    }
    setSelectedAdmin(adminWithDefaults)
    setUploadedAvatarUrl(null) // Reset uploaded avatar when opening modal
    // Initialize selected nationality with admin's nationality or default to tunisian
    setSelectedNationality(admin.nationality || 'tunisian')
    setNationalitySearchQuery('')
    setIsProfileModalOpen(true)
  }

  const handleFilterChange = (
    key: keyof AdminFilters,
    value: string,
    label: string
  ) => {
    setActiveFilters((prev) => {
      const filtered = prev.filter((f) => f.key !== key)
      if (value !== 'all') {
        return [...filtered, { key, value, label }]
      }
      return filtered
    })
    setCurrentPage(1)
  }

  const clearAllFilters = () => {
    setActiveFilters([])
    setSearchQuery('')
    setCurrentPage(1)
  }

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber)
  }

  const togglePasswordVisibility = () => {
    setIsPasswordVisible((prev) => !prev)
  }

  const handleDeleteClick = (admin: Admin) => {
    setAdminToDelete(admin)
    setIsDeleteDialogOpen(true)
  }

  const confirmDelete = () => {
    if (adminToDelete) {
      deleteAdminMutation.mutate(adminToDelete.id)
    }
  }

  const handleUpdateAdmin = async (data: Partial<Admin> & { password?: string }) => {
    if (!selectedAdmin?.id) return
    try {
      // Get the birthday from the form
      const formData = new FormData(
        document.querySelector('form') as HTMLFormElement
      )
      const birthday = formData.get('birthday') as string

      // Create update data object
      const updateData: UpdateAdminDTO = {
        firstname: data.firstname,
        lastname: data.lastname,
        email: data.email,
        cin: data.cin,
        avatar: data.avatar || DEFAULT_AVATAR,
        birthday: birthday ? new Date(birthday).toISOString() : undefined,
        gender: (data.gender?.toUpperCase() as 'MALE' | 'FEMALE') || 'MALE',
        address: data.address || '',
        phone: data.phone || '',
        nationality: data.nationality,
        // Keep the current admin's status since status field is removed from form
        isActive: selectedAdmin.isActive,
        updatedBy: localStorage.getItem('id') || null,
      }

      // Only include password if it was provided in the data object
      if (data.password) {
        updateData.password = data.password
      }

      // Do not include etablissementId as it will be automatically handled by the backend
      // The API will use the etablissementId from the JWT token

      await services.admin.update(selectedAdmin.id, updateData)

      queryClient.invalidateQueries({ queryKey: ['admins'] })
      setIsProfileModalOpen(false)
      toast({
        title: t('superAdminManagement.notifications.adminUpdated.title'),
        description: t('superAdminManagement.notifications.adminUpdated.description'),
        variant: 'default',
      })
    } catch (error) {
      // console.error('Failed to update admin:', error)

      // Check for 409 Conflict error specifically
      if (
        error &&
        typeof error === 'object' &&
        'response' in error &&
        error.response &&
        typeof error.response === 'object' &&
        'status' in error.response &&
        error.response.status === 409
      ) {
        // Use the imported parseConflictError function
        const errorMessage = parseConflictError(error)
        toast({
          title: t('superAdminManagement.notifications.updateFailed.title'),
          description: errorMessage,
          variant: 'destructive',
        })
      } else if (error && typeof error === 'object' && 'response' in error) {
        const errorResponse = error.response as { data?: { message?: any } }
        // console.error('Detailed error response:', errorResponse)
        const message = errorResponse.data?.message

        // Format the error message using our utility function
        const formattedMessage = formatErrorMessage(message)

        toast({
          title: t('superAdminManagement.notifications.updateFailed.title'),
          description: formattedMessage,
          variant: 'destructive',
        })
      } else {
        toast({
          title: t('superAdminManagement.notifications.error.title'),
          description: t('superAdminManagement.notifications.error.description'),
          variant: 'destructive',
        })
      }
    }
  }

  // Uncomment and use the handleCreateAdmin function
  const handleCreateAdmin = async (data: any) => {
    try {
      // Get the birthday from the form
      const birthday = data.birthday
      // Do not include etablissementId as it will be automatically handled by the backend
      // The API will use the etablissementId from the JWT token
      data.birthday = birthday
        ? new Date(birthday).toISOString()
        : new Date().toISOString()
      // Ensure gender is uppercase
      data.gender = data.gender.toUpperCase() as 'MALE' | 'FEMALE'
      // Ensure avatar is not undefined
      if (!data.avatar) {
        data.avatar = DEFAULT_AVATAR
      }
      // Add createdBy and updatedBy fields
      data.createdBy = localStorage.getItem('id') || null
      data.updatedBy = localStorage.getItem('id') || null
      // Set userOnboarding to true for new admins to show tour on first login
      // data.userOnboarding = true
      await services.admin.create(data)
      queryClient.invalidateQueries({ queryKey: ['admins'] })
      setIsAddAdminOpen(false)
      toast({
        title: t('superAdminManagement.notifications.adminCreated.title'),
        description: t('superAdminManagement.notifications.adminCreated.description'),
        variant: 'default',
      })
    } catch (error) {
      // console.error('Failed to create admin:', error)
      if (
        error &&
        typeof error === 'object' &&
        'response' in error &&
        error.response &&
        typeof error.response === 'object' &&
        'status' in error.response &&
        error.response.status === 409
      ) {
        const errorMessage = parseConflictError(error)
        toast({
          title: t('superAdminManagement.notifications.creationFailed.title'),
          description: errorMessage,
          variant: 'destructive',
        })
      } else if (error && typeof error === 'object' && 'response' in error) {
        const errorResponse = error.response as { data?: { message?: any } }
        const message = errorResponse.data?.message
        const formattedMessage = formatErrorMessage(message)
        toast({
          title: t('superAdminManagement.notifications.creationFailed.title'),
          description: formattedMessage,
          variant: 'destructive',
        })
      } else {
        toast({
          title: t('superAdminManagement.notifications.error.title'),
          description: t('superAdminManagement.notifications.error.description'),
          variant: 'destructive',
        })
      }
    }
  }

  // Simple function to display CIN value
  const displayCin = (admin: Admin): string => {
    // Just return the CIN directly from the admin object
    return admin.cin || 'N/A'
  }

  // Replace the virtualized render method with a standard table row render function
  const renderAdminRow = (admin: Admin) => {
    return (
      <tr key={admin.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
        <td className="py-4 px-4" data-tour="admin-info">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
              <CdnImage
                src={admin.avatar || DEFAULT_AVATAR}
                alt={`${admin.firstname} ${admin.lastname}`}
                className="w-full h-full object-cover"
                fallbackSrc={DEFAULT_AVATAR}
              />
            </div>
            <div className="flex flex-col">
              <div className="font-medium text-gray-900">
                {admin.firstname} {admin.lastname}
              </div>
              <div className="text-sm text-gray-500">{admin.email}</div>
            </div>
          </div>
        </td>
        <td className="py-4 px-4" data-tour="admin-cin">
          <div className="text-sm font-medium text-gray-900">
            {displayCin(admin)}
          </div>
        </td>
        <td className="py-4 px-4" data-tour="admin-status">
          <Badge 
            variant={admin.isActive ? 'success' : 'destructive'} 
            className={`${admin.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'} px-3 py-1 rounded-full text-sm font-medium`}
          >
            {admin.isActive ? t('superAdminManagement.table.status.active') : t('superAdminManagement.table.status.notActive')}
          </Badge>
        </td>
        <td className="py-4 px-4 text-right" data-tour="admin-actions">
          <div className="flex justify-end gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => handleShowProfile(admin)}
              className="text-blue-600 hover:text-blue-700"
            >
              {t('superAdminManagement.buttons.profile')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => toggleActiveMutation.mutate(admin.id)}
              className={admin.isActive ? 'text-red-600 hover:text-red-700' : 'text-green-600 hover:text-green-700'}
            >
              {admin.isActive ? t('superAdminManagement.buttons.setInactive') : t('superAdminManagement.buttons.setActive')}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => handleDeleteClick(admin)}
              className="bg-red-50 text-red-600 hover:bg-red-100"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </td>
      </tr>
    )
  }

  if (isLoading) return <Loading />
  if (isError)
    return (
              <p>
          {t('superAdminManagement.notifications.error.title')}:{' '}
          {error instanceof Error ? error.message : t('superAdminManagement.notifications.error.description')}
        </p>
    )

  return (
    <div className="p-4 sm:p-6">
      <Toaster />
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h2 className="text-xl sm:text-2xl font-bold">{t('superAdminManagement.pageTitle')}</h2>
        <Button onClick={() => setIsAddAdminOpen(true)} data-tour="add-admin-button" className="w-full sm:w-auto">
          {t('superAdminManagement.buttons.addAdmin')}
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1 w-full">
          <Input
            placeholder={t('superAdminManagement.search.placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full"
          />
        </div>

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="gap-2 w-full sm:w-auto" data-tour="search-section">
              <Filter className="h-4 w-4" />
              <span className="hidden sm:inline">{t('superAdminManagement.filters.title')}</span>
              <span className="sm:hidden">{t('superAdminManagement.filters.title')}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[calc(100vw-2rem)] sm:w-80">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{t('superAdminManagement.filters.name')}</Label>
                <Input
                  placeholder={`${t('superAdminManagement.filters.title')} ${t('superAdminManagement.filters.name').toLowerCase()}...`}
                  onChange={(e) =>
                    handleFilterChange('name', e.target.value, t('superAdminManagement.filters.name'))
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>{t('superAdminManagement.filters.cin')}</Label>
                <Input
                  placeholder={`${t('superAdminManagement.filters.title')} ${t('superAdminManagement.filters.cin').toLowerCase()}...`}
                  onChange={(e) =>
                    handleFilterChange('cin', e.target.value, t('superAdminManagement.filters.cin'))
                  }
                />
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0 mt-6">
        <div className="flex-1">
          <Badge variant="secondary" className="text-sm">
            {t('superAdminManagement.pagination.adminsCount', { count: filteredAdmins.length })}
          </Badge>
        </div>
        <div className="flex-1 sm:text-right">
          <Badge variant="secondary" className="text-sm">
            {t('superAdminManagement.pagination.page', { current: currentPage, total: totalPages })}
          </Badge>
        </div>
      </div>

      <div className="flex justify-center gap-2 mt-6">
        <Button
          variant="outline"
          size="icon"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {activeFilters.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4">
          {activeFilters.map((filter) => (
            <Badge
              key={filter.key}
              variant="secondary"
              className="flex items-center gap-1"
            >
              <span className="hidden sm:inline">{filter.label}: </span>
              <span className="truncate max-w-[150px] sm:max-w-none">{filter.value}</span>
              <X
                className="h-3 w-3 cursor-pointer flex-shrink-0"
                onClick={() =>
                  handleFilterChange(filter.key, 'all', filter.label)
                }
              />
            </Badge>
          ))}
          <Button
            variant="ghost"
            size="sm"
            className="h-6"
            onClick={clearAllFilters}
          >
            {t('superAdminManagement.filters.clearAll')}
          </Button>
        </div>
      )}

      {/* Desktop Table View */}
      <div className="mt-6 hidden md:block overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-50">
              <th className="text-left py-3 px-4 font-semibold" data-tour="admin-info">{t('superAdminManagement.table.headers.adminInfo')}</th>
              <th className="text-left py-3 px-4 font-semibold" data-tour="admin-cin">{t('superAdminManagement.table.headers.cin')}</th>
              <th className="text-left py-3 px-4 font-semibold" data-tour="admin-status">{t('superAdminManagement.table.headers.workStatus')}</th>
              <th className="text-right py-3 px-4 font-semibold" data-tour="admin-actions">{t('superAdminManagement.table.headers.actions')}</th>
            </tr>
          </thead>
          <tbody>{currentAdmins.map(renderAdminRow)}</tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="mt-6 md:hidden space-y-4">
        {currentAdmins.map((admin) => (
          <div key={admin.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
                <CdnImage
                  src={admin.avatar || DEFAULT_AVATAR}
                  alt={`${admin.firstname} ${admin.lastname}`}
                  className="w-full h-full object-cover"
                  fallbackSrc={DEFAULT_AVATAR}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 truncate">
                  {admin.firstname} {admin.lastname}
                </div>
                <div className="text-sm text-gray-500 truncate">{admin.email}</div>
              </div>
            </div>
            <div className="space-y-2 mb-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">{t('superAdminManagement.table.headers.cin')}:</span>
                <span className="text-sm font-medium text-gray-900">{displayCin(admin)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">{t('superAdminManagement.table.headers.workStatus')}:</span>
                <Badge 
                  variant={admin.isActive ? 'success' : 'destructive'} 
                  className={`${admin.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'} px-3 py-1 rounded-full text-sm font-medium`}
                >
                  {admin.isActive ? t('superAdminManagement.table.status.active') : t('superAdminManagement.table.status.notActive')}
                </Badge>
              </div>
            </div>
            <div className="flex flex-col gap-2 pt-4 border-t border-gray-200">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleShowProfile(admin)}
                className="text-blue-600 hover:text-blue-700 w-full"
              >
                {t('superAdminManagement.buttons.profile')}
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleActiveMutation.mutate(admin.id)}
                  className={`flex-1 ${admin.isActive ? 'text-red-600 hover:text-red-700' : 'text-green-600 hover:text-green-700'}`}
                >
                  {admin.isActive ? t('superAdminManagement.buttons.setInactive') : t('superAdminManagement.buttons.setActive')}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleDeleteClick(admin)}
                  className="bg-red-50 text-red-600 hover:bg-red-100"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Profile Modal */}
      <Dialog open={isProfileModalOpen} onOpenChange={(open) => {
        setIsProfileModalOpen(open)
        if (!open) {
          // Reset nationality search when modal closes
          setNationalitySearchQuery('')
        }
      }}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('superAdminManagement.profileModal.title')}</DialogTitle>
          </DialogHeader>
          {selectedAdmin && (
            <form
              key={selectedAdmin.id}
              onSubmit={(e) => {
                e.preventDefault()
                const formData = new FormData(e.currentTarget)
                const gender = formData.get('gender') as string
                // console.log('Form Gender:', gender)
                // console.log('Current Admin Gender:', selectedAdmin.gender)

                // Get the avatar URL from the hidden input or uploaded state
                const avatarInput = document.getElementById(
                  'edit-avatar'
                ) as HTMLInputElement
                const avatarUrl = uploadedAvatarUrl || 
                  (avatarInput?.value) || 
                  selectedAdmin.avatar || 
                  DEFAULT_AVATAR
                // console.log('Final avatar URL being used:', avatarUrl)

                // Create a date object to format it properly
                const birthdayStr = formData.get('birthday') as string
                
                // Validate birthday is at least 5 years ago
                if (birthdayStr) {
                  // Parse YYYY-MM-DD format as local date
                  const [year, month, day] = birthdayStr.split('-').map(Number)
                  const birthdayDate = new Date(year, month - 1, day)
                  const today = new Date()
                  const fiveYearsAgo = new Date(today.getFullYear() - 5, today.getMonth(), today.getDate())
                  
                  if (birthdayDate > fiveYearsAgo) {
                    toast({
                      title: t('superAdminManagement.notifications.error.title'),
                      description: 'Date of birth must be at least 5 years ago.',
                      variant: 'destructive',
                    })
                    return
                  }
                }
                
                // Parse date in local timezone to avoid timezone offset issues
                let birthdayISO: string | undefined
                if (birthdayStr) {
                  // Parse YYYY-MM-DD format as local date
                  const [year, month, day] = birthdayStr.split('-').map(Number)
                  const birthdayDate = new Date(year, month - 1, day)
                  // Format as ISO string
                  birthdayISO = birthdayDate.toISOString()
                }

                // Get nationality from selected value and validate
                const nationality = selectedNationality || selectedAdmin.nationality || 'tunisian'
                
                // Validate nationality is a valid key
                if (!getAllNationalityKeys().includes(nationality)) {
                  toast({
                    title: t('superAdminManagement.notifications.error.title'),
                    description: 'Please select a valid nationality from the list.',
                    variant: 'destructive',
                  })
                  return
                }

                const data: any = {
                  firstname: formData.get('firstname') as string,
                  lastname: formData.get('lastname') as string,
                  email: formData.get('email') as string,
                  avatar: avatarUrl || DEFAULT_AVATAR,
                  // Send birthday as ISO string, not as Date object
                  birthday: birthdayISO,
                  gender: gender?.toUpperCase() as 'MALE' | 'FEMALE',
                  // Include CIN from form data
                  cin: formData.get('cin') as string,
                  address: formData.get('address') as string || '',
                  phone: formData.get('phone') as string || '',
                  nationality: nationality,
                }

                // Only add password if it has a value
                const passwordField = document.getElementById('password-edit') as HTMLInputElement
                if (passwordField && passwordField.value.trim() !== '') {
                  data.password = passwordField.value.trim()
                }

                // console.log('Updating admin with data:', data)
                handleUpdateAdmin(data)
              }}
              className="space-y-4"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstname">{t('superAdminManagement.profileModal.fields.firstname.label')}</Label>
                  <Input
                    id="firstname"
                    name="firstname"
                    defaultValue={selectedAdmin.firstname}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastname">{t('superAdminManagement.profileModal.fields.lastname.label')}</Label>
                  <Input
                    id="lastname"
                    name="lastname"
                    defaultValue={selectedAdmin.lastname}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">{t('superAdminManagement.profileModal.fields.email.label')}</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  defaultValue={selectedAdmin.email}
                />
              </div>
              
              {/* CIN and Nationality on same line */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cin">{t('superAdminManagement.profileModal.fields.cin.label')}</Label>
                  <Input
                    id="cin"
                    name="cin"
                    defaultValue={selectedAdmin.cin}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="nationality">{t('superAdminManagement.profileModal.fields.nationality.label')}</Label>
                  <Popover open={nationalitySearchOpen} onOpenChange={setNationalitySearchOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={nationalitySearchOpen}
                        className="w-full justify-between"
                        type="button"
                      >
                        {(() => {
                          const currentValue = selectedNationality || selectedAdmin.nationality || 'tunisian'
                          if (getAllNationalityKeys().includes(currentValue)) {
                            return tNationalities(`nationalities.${currentValue}`, currentValue)
                          }
                          return t('superAdminManagement.profileModal.fields.nationality.placeholder')
                        })()}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0" align="start">
                      <div className="p-2">
                        <Input
                          placeholder={t('superAdminManagement.profileModal.fields.nationality.placeholder')}
                          value={nationalitySearchQuery}
                          onChange={(e) => setNationalitySearchQuery(e.target.value)}
                          className="mb-2"
                        />
                      </div>
                      <ScrollArea className="h-[200px]">
                        <div className="p-1">
                          {getAllNationalityKeys()
                            .filter((key) => {
                              if (!nationalitySearchQuery) return true
                              const translated = tNationalities(`nationalities.${key}`, key).toLowerCase()
                              const searchLower = nationalitySearchQuery.toLowerCase()
                              return translated.includes(searchLower) || key.toLowerCase().includes(searchLower)
                            })
                            .map((key) => {
                              const translated = tNationalities(`nationalities.${key}`, key)
                              const currentValue = selectedNationality || selectedAdmin.nationality || 'tunisian'
                              const isSelected = currentValue === key
                              return (
                                <button
                                  key={key}
                                  type="button"
                                  className={cn(
                                    'w-full flex items-center justify-between px-2 py-1.5 text-sm rounded-sm hover:bg-accent hover:text-accent-foreground cursor-pointer',
                                    isSelected && 'bg-accent'
                                  )}
                                  onClick={() => {
                                    setSelectedNationality(key)
                                    setNationalitySearchQuery('')
                                    setNationalitySearchOpen(false)
                                  }}
                                >
                                  <span>{translated}</span>
                                  {isSelected && <Check className="h-4 w-4" />}
                                </button>
                              )
                            })}
                          {nationalitySearchQuery && getAllNationalityKeys().filter((key) => {
                            const translated = tNationalities(`nationalities.${key}`, key).toLowerCase()
                            const searchLower = nationalitySearchQuery.toLowerCase()
                            return translated.includes(searchLower) || key.toLowerCase().includes(searchLower)
                          }).length === 0 && (
                            <div className="px-2 py-1.5 text-sm text-gray-500">
                              No nationality found
                            </div>
                          )}
                        </div>
                      </ScrollArea>
                    </PopoverContent>
                  </Popover>
                  <Input
                    id="nationality-hidden"
                    name="nationality"
                    type="hidden"
                    defaultValue={selectedAdmin.nationality || 'tunisian'}
                  />
                </div>
              </div>

              {/* Avatar */}
              <div className="space-y-2">
                <FileUploader
                  label={t('superAdminManagement.profileModal.fields.avatar.label')}
                  previewUrl={uploadedAvatarUrl || selectedAdmin.avatar || DEFAULT_AVATAR}
                  onFileUploaded={(url) => {
                    // console.log('FileUploader callback called with URL:', url)
                    // Extract the actual CDN URL from proxy URL if needed
                    let actualUrl = url
                    if (url.includes('/health/proxy/image?url=')) {
                      actualUrl = decodeURIComponent(url.split('url=')[1])
                    }
                    // console.log('Extracted CDN URL:', actualUrl)
                    
                    // Update state to show the uploaded image
                    setUploadedAvatarUrl(actualUrl)
                    
                    // Store the uploaded file URL in a hidden input
                    const avatarInput = document.getElementById(
                      'edit-avatar'
                    ) as HTMLInputElement
                    if (avatarInput) {
                      avatarInput.value = actualUrl
                      // console.log('Updated hidden input value to:', actualUrl)
                    } else {
                      // console.error('Avatar input element not found!')
                    }
                  }}
                  isAvatar={true}
                  defaultPreview={selectedAdmin.avatar || DEFAULT_AVATAR}
                />
                <Input
                  id="edit-avatar"
                  name="avatar"
                  type="hidden"
                  defaultValue={selectedAdmin.avatar || DEFAULT_AVATAR}
                />
              </div>

              {/* Birthday and Gender on same line */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="birthday">{t('superAdminManagement.profileModal.fields.birthday.label')}</Label>
                  <FormYearMonthPicker
                    id="birthday"
                    label=""
                    value={
                      selectedAdmin?.birthday
                        ? new Date(selectedAdmin.birthday)
                        : (() => {
                            // Default to 5 years ago if no birthday is set
                            const today = new Date()
                            return new Date(today.getFullYear() - 5, today.getMonth(), today.getDate())
                          })()
                    }
                    onChange={() => {
                      // onChange={(date: Date | undefined) => {
                      // The picker will now automatically update the hidden input
                      // const formData = new FormData(
                      //   document.querySelector('form') as HTMLFormElement
                      // )
                      // console.log(
                      //   'Selected date:',
                      //   date,
                      //   'Form data birthday:',
                      //   formData.get('birthday')
                      // )
                    }}
                    placeholder={t('superAdminManagement.profileModal.fields.birthday.label')}
                    maxDate={(() => {
                      const today = new Date()
                      return new Date(today.getFullYear() - 5, today.getMonth(), today.getDate())
                    })()}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gender">{t('superAdminManagement.profileModal.fields.gender.label')}</Label>
                  <Select
                    name="gender"
                    defaultValue={selectedAdmin.gender?.toUpperCase() || 'MALE'}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t('superAdminManagement.profileModal.fields.gender.label')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MALE">{t('superAdminManagement.profileModal.fields.gender.options.male')}</SelectItem>
                      <SelectItem value="FEMALE">{t('superAdminManagement.profileModal.fields.gender.options.female')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Address and Phone on same line */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="address">{t('superAdminManagement.profileModal.fields.address.label')}</Label>
                  <Input
                    id="address"
                    name="address"
                    defaultValue={selectedAdmin.address ?? ''}
                    placeholder={t('superAdminManagement.profileModal.fields.address.placeholder')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">{t('superAdminManagement.profileModal.fields.phone.label')}</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    defaultValue={selectedAdmin.phone ?? ''}
                    placeholder={t('superAdminManagement.profileModal.fields.phone.placeholder')}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password-edit">{t('superAdminManagement.profileModal.fields.password.label')}</Label>
                <div className="relative">
                  <Input
                    id="password-edit"
                    name="password"
                    type={isPasswordVisible ? 'text' : 'password'}
                    placeholder={t('superAdminManagement.profileModal.fields.password.placeholder')}
                    onFocus={() => setIsPasswordFocused(true)}
                    onBlur={() => setIsPasswordFocused(false)}
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                  >
                    {isPasswordVisible ? (
                      <EyeOff className="h-4 w-4 text-gray-500" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-500" />
                    )}
                  </button>
                </div>

                {/* Password requirements - only show when field is focused */}
                {isPasswordFocused && (
                  <div className="mt-2 text-xs text-gray-500">
                    <p>{t('superAdminManagement.profileModal.passwordRequirements.title')}</p>
                    <ul className="list-disc pl-5 space-y-1 mt-1">
                      <li>{t('superAdminManagement.profileModal.passwordRequirements.length')}</li>
                      <li>{t('superAdminManagement.profileModal.passwordRequirements.uppercase')}</li>
                      <li>{t('superAdminManagement.profileModal.passwordRequirements.number')}</li>
                      <li>{t('superAdminManagement.profileModal.passwordRequirements.special')}</li>
                    </ul>
                  </div>
                )}
              </div>
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-4 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsProfileModalOpen(false)}
                  className="w-full sm:w-auto"
                >
                  {t('superAdminManagement.profileModal.buttons.cancel')}
                </Button>
                <Button type="submit" className="w-full sm:w-auto">{t('superAdminManagement.profileModal.buttons.update')}</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Add New Admin Modal */}
      <Dialog open={isAddAdminOpen} onOpenChange={(open) => {
        setIsAddAdminOpen(open)
        if (!open) {
          // Reset nationality search when modal closes
          setCreateNationalitySearchQuery('')
          setCreateUploadedAvatarUrl(null) // Reset uploaded avatar
        } else {
          // Initialize with default tunisian
          setCreateSelectedNationality('tunisian')
          setCreateNationalitySearchQuery('')
          setCreateUploadedAvatarUrl(null) // Reset uploaded avatar
        }
      }}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('superAdminManagement.addAdminModal.title')}</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              
              // Get nationality from selected value and validate
              const nationality = createSelectedNationality || 'tunisian'
              
              // Validate nationality is a valid key
              if (!getAllNationalityKeys().includes(nationality)) {
                toast({
                  title: t('superAdminManagement.notifications.error.title'),
                  description: 'Please select a valid nationality from the list.',
                  variant: 'destructive',
                })
                return
              }
              
              // Validate birthday is at least 5 years ago
              const birthdayStr = formData.get('birthday') as string
              if (birthdayStr) {
                // Parse YYYY-MM-DD format as local date
                const [year, month, day] = birthdayStr.split('-').map(Number)
                const birthdayDate = new Date(year, month - 1, day)
                const today = new Date()
                const fiveYearsAgo = new Date(today.getFullYear() - 5, today.getMonth(), today.getDate())
                
                if (birthdayDate > fiveYearsAgo) {
                  toast({
                    title: t('superAdminManagement.notifications.error.title'),
                    description: 'Date of birth must be at least 5 years ago.',
                    variant: 'destructive',
                  })
                  return
                }
              }
              
              const data: any = {
                firstname: formData.get('firstname'),
                lastname: formData.get('lastname'),
                email: formData.get('email'),
                password: formData.get('password'),
                cin: formData.get('cin'),
                gender: formData.get('gender'),
                birthday: formData.get('birthday'),
                address: formData.get('address'),
                phone: formData.get('phone'),
                nationality: nationality,
                avatar: createUploadedAvatarUrl || (formData.get('avatar') as string) || DEFAULT_AVATAR,
                isActive: formData.get('isActive') === 'true',
              };
              await handleCreateAdmin(data);
            }}
            className="space-y-4"
          >
            {/* Name Fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstname">{t('superAdminManagement.addAdminModal.fields.firstname.label')}</Label>
                <Input id="firstname" name="firstname" required data-tour="admin-form-firstname" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastname">{t('superAdminManagement.addAdminModal.fields.lastname.label')}</Label>
                <Input id="lastname" name="lastname" required data-tour="admin-form-firstname" />
              </div>
            </div>

            {/* Email and Password */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t('superAdminManagement.addAdminModal.fields.email.label')}</Label>
                <Input id="email" name="email" type="email" required data-tour="admin-form-email" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t('superAdminManagement.addAdminModal.fields.password.label')}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={isPasswordVisible ? 'text' : 'password'}
                    required
                    onFocus={() => setIsPasswordFocused(true)}
                    onBlur={() => setIsPasswordFocused(false)}
                    data-tour="admin-form-password"
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                  >
                    {isPasswordVisible ? (
                      <EyeOff className="h-4 w-4 text-gray-500" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-500" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* CIN and Gender */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cin">{t('superAdminManagement.addAdminModal.fields.cin.label')}</Label>
                <Input id="cin" name="cin" required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="gender">{t('superAdminManagement.addAdminModal.fields.gender.label')}</Label>
                <Select name="gender" defaultValue="MALE">
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MALE">{t('superAdminManagement.addAdminModal.fields.gender.options.male')}</SelectItem>
                    <SelectItem value="FEMALE">{t('superAdminManagement.addAdminModal.fields.gender.options.female')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Birthday */}
            <div className="space-y-2">
              <Label htmlFor="birthday">{t('superAdminManagement.addAdminModal.fields.birthday.label')}</Label>
              <FormYearMonthPicker
                id="birthday"
                label=""
                value={(() => {
                  // Set default value to 5 years ago
                  const today = new Date()
                  const fiveYearsAgo = new Date(today.getFullYear() - 5, today.getMonth(), today.getDate())
                  return fiveYearsAgo
                })()}
                onChange={(date: Date | undefined) => {
                  // console.log('Date selected in picker:', date)
                  if (date) {
                    const birthdayInput = document.querySelector(
                      'input[name="birthday"]'
                    ) as HTMLInputElement
                    if (birthdayInput) {
                      // Format in local timezone to avoid timezone offset
                      const year = date.getFullYear()
                      const month = String(date.getMonth() + 1).padStart(2, '0')
                      const day = String(date.getDate()).padStart(2, '0')
                      birthdayInput.value = `${year}-${month}-${day}`
                    }
                  }
                }}
                placeholder={t('superAdminManagement.addAdminModal.fields.birthday.label')}
                required={false}
                maxDate={(() => {
                  const today = new Date()
                  return new Date(today.getFullYear() - 5, today.getMonth(), today.getDate())
                })()}
              />
              {/* Hidden input to store the birthday value */}
              <Input
                type="hidden"
                name="birthday"
                defaultValue={(() => {
                  // Set default value to 5 years ago in YYYY-MM-DD format
                  const today = new Date()
                  const fiveYearsAgo = new Date(today.getFullYear() - 5, today.getMonth(), today.getDate())
                  const year = fiveYearsAgo.getFullYear()
                  const month = String(fiveYearsAgo.getMonth() + 1).padStart(2, '0')
                  const day = String(fiveYearsAgo.getDate()).padStart(2, '0')
                  return `${year}-${month}-${day}`
                })()}
              />
            </div>

            {/* Address and Phone */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="address">{t('superAdminManagement.addAdminModal.fields.address.label')}</Label>
                <Input id="address" name="address" placeholder={t('superAdminManagement.addAdminModal.fields.address.placeholder')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">{t('superAdminManagement.addAdminModal.fields.phone.label')}</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder={t('superAdminManagement.addAdminModal.fields.phone.placeholder')}
                />
              </div>
            </div>

            {/* Nationality */}
            <div className="space-y-2">
              <Label htmlFor="nationality">{t('superAdminManagement.addAdminModal.fields.nationality.label')}</Label>
              <Popover open={createNationalitySearchOpen} onOpenChange={setCreateNationalitySearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={createNationalitySearchOpen}
                    className="w-full justify-between"
                    type="button"
                  >
                    {(() => {
                      const currentValue = createSelectedNationality || 'tunisian'
                      if (getAllNationalityKeys().includes(currentValue)) {
                        return tNationalities(`nationalities.${currentValue}`, currentValue)
                      }
                      return t('superAdminManagement.addAdminModal.fields.nationality.placeholder')
                    })()}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <div className="p-2">
                    <Input
                      placeholder={t('superAdminManagement.addAdminModal.fields.nationality.placeholder')}
                      value={createNationalitySearchQuery}
                      onChange={(e) => setCreateNationalitySearchQuery(e.target.value)}
                      className="mb-2"
                    />
                  </div>
                  <ScrollArea className="h-[200px]">
                    <div className="p-1">
                      {getAllNationalityKeys()
                        .filter((key) => {
                          if (!createNationalitySearchQuery) return true
                          const translated = tNationalities(`nationalities.${key}`, key).toLowerCase()
                          const searchLower = createNationalitySearchQuery.toLowerCase()
                          return translated.includes(searchLower) || key.toLowerCase().includes(searchLower)
                        })
                        .map((key) => {
                          const translated = tNationalities(`nationalities.${key}`, key)
                          const currentValue = createSelectedNationality || 'tunisian'
                          const isSelected = currentValue === key
                          return (
                            <button
                              key={key}
                              type="button"
                              className={cn(
                                'w-full flex items-center justify-between px-2 py-1.5 text-sm rounded-sm hover:bg-accent hover:text-accent-foreground cursor-pointer',
                                isSelected && 'bg-accent'
                              )}
                              onClick={() => {
                                setCreateSelectedNationality(key)
                                setCreateNationalitySearchQuery('')
                                setCreateNationalitySearchOpen(false)
                              }}
                            >
                              <span>{translated}</span>
                              {isSelected && <Check className="h-4 w-4" />}
                            </button>
                          )
                        })}
                      {createNationalitySearchQuery && getAllNationalityKeys().filter((key) => {
                        const translated = tNationalities(`nationalities.${key}`, key).toLowerCase()
                        const searchLower = createNationalitySearchQuery.toLowerCase()
                        return translated.includes(searchLower) || key.toLowerCase().includes(searchLower)
                      }).length === 0 && (
                        <div className="px-2 py-1.5 text-sm text-gray-500">
                          No nationality found
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </PopoverContent>
              </Popover>
              <Input
                id="create-nationality-hidden"
                name="nationality"
                type="hidden"
                defaultValue="tunisian"
              />
            </div>

            {/* Avatar Upload */}
            <div className="space-y-2">
              <FileUploader
                label={t('superAdminManagement.addAdminModal.fields.avatar.label')}
                previewUrl={createUploadedAvatarUrl || DEFAULT_AVATAR}
                onFileUploaded={(url) => {
                  // console.log('FileUploader callback called with URL:', url)
                  // Extract the actual CDN URL from proxy URL if needed
                  let actualUrl = url
                  if (url.includes('/health/proxy/image?url=')) {
                    actualUrl = decodeURIComponent(url.split('url=')[1])
                  }
                  // console.log('Extracted CDN URL:', actualUrl)
                  
                  // Update state to show the uploaded image
                  setCreateUploadedAvatarUrl(actualUrl)
                  
                  const avatarInput = document.getElementById(
                    'avatar'
                  ) as HTMLInputElement
                  if (avatarInput) {
                    avatarInput.value = actualUrl
                    const event = new Event('input', { bubbles: true })
                    avatarInput.dispatchEvent(event)
                    // console.log('Updated hidden input value to:', actualUrl)
                  } else {
                    // console.error('Avatar input element not found!')
                  }
                }}
                // onError={(error) => {
                //   // console.error('Avatar upload error:', error)
                // }}
                isAvatar={true}
                defaultPreview={DEFAULT_AVATAR}
              />
              <Input
                id="avatar"
                name="avatar"
                type="hidden"
                defaultValue={DEFAULT_AVATAR}
                required
              />
              <div className="text-xs text-gray-500 mt-1">
                {t('superAdminManagement.addAdminModal.fields.avatar.note')}
              </div>
            </div>

            {/* Password requirements - only show when field is focused */}
            {isPasswordFocused && (
              <div className="text-xs text-gray-500">
                <p>{t('superAdminManagement.addAdminModal.passwordRequirements.title')}</p>
                <ul className="list-disc pl-5 space-y-1 mt-1">
                  <li id="length-check">{t('superAdminManagement.addAdminModal.passwordRequirements.length')}</li>
                  <li id="uppercase-check">{t('superAdminManagement.addAdminModal.passwordRequirements.uppercase')}</li>
                  <li id="number-check">{t('superAdminManagement.addAdminModal.passwordRequirements.number')}</li>
                  <li id="special-check">{t('superAdminManagement.addAdminModal.passwordRequirements.special')}</li>
                </ul>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsAddAdminOpen(false)}
                className="w-full sm:w-auto"
              >
                {t('superAdminManagement.addAdminModal.buttons.cancel')}
              </Button>
              <Button type="submit" data-tour="admin-form-submit" className="w-full sm:w-auto">{t('superAdminManagement.addAdminModal.buttons.create')}</Button>
            </div>
          </form>
          {isAddAdminOpen && <AddAdminTour />}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('superAdminManagement.deleteDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('superAdminManagement.deleteDialog.description', {
                adminName: adminToDelete ? `${adminToDelete.firstname} ${adminToDelete.lastname}` : ''
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('superAdminManagement.deleteDialog.buttons.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              {t('superAdminManagement.deleteDialog.buttons.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

